import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { UsersUserResponse } from '@libs/generated-types/users';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { SelfserviceDraftSelfServiceSubmission, SelfserviceSelfServiceSubmission } from '../../../../../../../../../libs/shared/generated/src/lib/types/selfservice';
import { PreEmploymentActivitiesService } from '../../../../../services/pre-employment-activities.service';
import { AddressDeclarationKind, RecrutimentSelfServiceType } from '../../../../data/recruitment';
import { BankDetailsChangePaymentMethod } from '../../../../data/selfservice-forms/bank-details-change';
import { SELF_SERVICE_MAPPERS, SELF_SERVICE_REVERSE_MAPPERS } from '../../pre-employment-selfservice.mappers';
import { AddressDeclarationComponent } from "./forms/address-declaration/address-declaration.component";
import { CommerceTaxReliefComponent } from './forms/commerce-tax-relief/commerce-tax-relief.component';
import type { EmploymentHistoryField } from './forms/employment-history/employment-history.component';
import { EmploymentHistoryComponent } from "./forms/employment-history/employment-history.component";
import { FirstEmploymentComponent } from "./forms/first-employment/first-employment.component";
import { PaymentDetailsComponent } from "./forms/payment-details/payment-details.component";
import { PersonalContactsComponent } from "./forms/personal-contacts/personal-contacts.component";
import { PreviousWorkActivitiesComponent } from "./forms/previous-work-activities/previous-work-activities.component";
import { RetirementPlanComponent } from "./forms/retirement-plan/retirement-plan.component";
import {
  TfrDeclarationComponent,
  tfrDeclarationOptionsValidator,
} from "./forms/tfr-declaration/tfr-declaration.component";
import { TourismTaxReliefComponent } from './forms/tourism-tax-relief/tourism-tax-relief.component';

@Component({
  selector: 'app-pre-employment-selfservice-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    AddressDeclarationComponent,
    CommerceTaxReliefComponent,
    EmploymentHistoryComponent,
    FirstEmploymentComponent,
    PaymentDetailsComponent,
    PersonalContactsComponent,
    PreviousWorkActivitiesComponent,
    RetirementPlanComponent,
    TfrDeclarationComponent,
    TourismTaxReliefComponent,
    TranslateModule,
    ToastModule
  ],
  templateUrl: './pre-employment-selfservice-dialog.component.html',
  styleUrls: ['./pre-employment-selfservice-dialog.component.scss'],
  standalone: true,
})
export class PreEmploymentSelfserviceDialogComponent implements OnInit {

  @Input() open: boolean = false;
  @Input() selfServiceType: RecrutimentSelfServiceType;
  @Input() activity: any;
  @Input() me: UsersUserResponse | null = null;
  @Input() requestId?: string | null = null;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitted: EventEmitter<void> = new EventEmitter<void>();
  formLoaded: boolean = false;
  selfServiceForm: FormGroup;
  isSaving: boolean = false
  isCompleted: boolean = false
  isDraft: boolean = false;
  tfrConfirmationVisible: boolean = false;
  tfrInCompanyModificationMode: boolean = false;
  // used for html template to access enum values
  RecrutimentSelfServiceType = RecrutimentSelfServiceType;
  hiredAtYear: number;
  confirmedEmploymentHistoryFields: EmploymentHistoryField[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly messageService: MessageService,
    private readonly preEmploymentActivitiesService: PreEmploymentActivitiesService,
    private readonly translate: TranslateService
  ) { }

  ngOnInit(): void {
    if (!this.requestId) {
      this.initializeEmptyForm();
    } else {
      this.initializeFormWithRequestId(this.requestId);
    }
    this.hiredAtYear = this.me?.hiredAt ? new Date(this.me.hiredAt).getFullYear() : new Date().getFullYear();
  }

  private initializeEmptyForm(): void {
    this.tfrInCompanyModificationMode = false;
    this.isCompleted = false;
    this.isDraft = false;
    this.confirmedEmploymentHistoryFields = [];
    switch (this.selfServiceType) {
      case RecrutimentSelfServiceType.ADDRESS_DECLARATION:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [AddressDeclarationKind.ITALIAN, Validators.required],
          stateOrRegion: [null, Validators.required],
          provinceOrCounty: [null, Validators.required],
          city: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.EMPLOYMENT_HISTORY:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [null, Validators.required],
          sickDaysCount: [null],
          taxableSocialSecurityAmount: [null],
          fringeBenefitsAmount: [null],
          hasSeverancePay: [null],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.FIRST_EMPLOYMENT:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          isBeforeJan2007: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.PAYMENT_DETAILS_CHANGE:

        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          kind: [BankDetailsChangePaymentMethod.BANK_TRANSFER, Validators.required],
          notesAcknowledged: [false, Validators.requiredTrue],
          accountHolder: this.fb.group({
            firstName: [null, Validators.required],
            lastName: [null, Validators.required],
          }),
          accountCoHolder: this.fb.group({
            firstName: [null],
            lastName: [null],
          }),
          iban: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.PERSONAL_CONTACTS_DETAILS:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          notesAcknowledged: [false, Validators.requiredTrue],
          landlinePhone: [null],
          email: [null, Validators.required],
          mobilePhone: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.PREVIOUS_WORK_ACTIVITIES:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          noSubordinateEmployment: [null, Validators.required],
          noSubordinateEmploymentUnder6Months: [null, Validators.required],
          selfEmployment: [null, Validators.required],
          coordinatedCollaborationContract: [null, Validators.required],
          hasCompletedSecondarySchool: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.RETIREMENT_PLAN:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          contributionsBefore1996: [null, Validators.required],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.TFR_DECLARATION:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          firstTime: [false],
          alreadyInCompany: [false],
          ransom: [false],
          notRansom: [false],
          tfrPrimaryChoice: [null],
          inCompany: [false],
          createdAt: [new Date()]
        }, { validators: tfrDeclarationOptionsValidator });
        break;
      case RecrutimentSelfServiceType.DETASSAZIONE_COMMERCIO:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          incomeAcceptance: [false, Validators.requiredTrue],
          income: [null],
          alreadyTaxedSubstituteTaxAcceptance: [false, Validators.requiredTrue],
          alreadyTaxedSubstituteTaxAmount: [null],
          reliefAcceptance: [false],
          acceptance: [false, Validators.requiredTrue],
          identityCardAttachments: [[], Validators.required],
          nightOrHolidayWorkAcceptance: [false],
          createdAt: [new Date()]
        });
        break;
      case RecrutimentSelfServiceType.DETASSAZIONE_TURISMO:
        this.selfServiceForm = this.fb.group({
          type: this.selfServiceType,
          incomeAcceptance: [false, Validators.requiredTrue],
          income: [null],
          reliefAcceptance: [false],
          acceptance: [false, Validators.requiredTrue],
          identityCardAttachments: [[], Validators.required],
          createdAt: [new Date()]
        });
        break;
      default:
        throw new Error('Invalid selfServiceType');
    }
    this.formLoaded = true;
  }

  private initializeFormWithRequestId(requestId: string): void {
    this.initializeEmptyForm();

    this.preEmploymentActivitiesService.getActivityDetail(requestId).subscribe({
      next: (response) => {
        const submission = response.submission;
        if (!submission || submission.activityKind !== 'SELF_SERVICE') {
          return;
        }
        this.isDraft = submission.status === 'draft';
        this.isCompleted = !this.isDraft;

        const reverseMapper = SELF_SERVICE_REVERSE_MAPPERS[this.selfServiceType]
        if (reverseMapper) this.selfServiceForm.patchValue(reverseMapper(submission.selfService as any))

        if (
          this.selfServiceType === RecrutimentSelfServiceType.EMPLOYMENT_HISTORY
          && submission.selfService.selfServiceKind === 'EMPLOYMENT_HISTORY'
        ) {
          this.confirmedEmploymentHistoryFields = Object.keys(
            submission.selfService.employmentHistoryDetails,
          ) as EmploymentHistoryField[];
        }

        if (this.selfServiceType === RecrutimentSelfServiceType.TFR_DECLARATION) {
          this.selfServiceForm.patchValue({ createdAt: new Date(submission.createdAt) });
          this.enableTfrInCompanyModificationIfEligible();
        }

        if (this.isCompleted) {
          this.selfServiceForm.disable();
          this.enableTfrInCompanyModificationIfEligible();
        }
      },
      error: () => this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
        detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
      }),
    });
  }

  save(): void {
    if (this.selfServiceType === RecrutimentSelfServiceType.TFR_DECLARATION) {
      if (this.selfServiceForm.invalid || (this.tfrInCompanyModificationMode && !this.isTfrInCompanySelected())) {
        this.selfServiceForm.markAllAsTouched();
        return;
      }

      this.tfrConfirmationVisible = true;
      return;
    }

    this.submit();
  }

  onEmploymentHistoryCompleted(): void {
    this.submitted.emit();
  }

  confirmTfrSubmission(): void {
    this.tfrConfirmationVisible = false;

    if (this.tfrInCompanyModificationMode) {
      this.updateTfrDeclarationSubmission();
      return;
    }

    this.submit();
  }

  cancelTfrConfirmation(): void {
    this.tfrConfirmationVisible = false;
  }

  saveDraft(): void {
    if (!this.requestId || this.isCompleted) return;

    const mapper = SELF_SERVICE_MAPPERS[this.selfServiceType]

    if (!mapper) return;

    const payload: SelfserviceDraftSelfServiceSubmission = {
      activityKind: 'SELF_SERVICE',
      status: 'draft',
      selfService: mapper(this.selfServiceForm.getRawValue()),
    };

    this.isSaving = true;

    this.preEmploymentActivitiesService.saveDraft(this.requestId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = true;
        this.submitted.emit();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-draft-saved'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-draft-saved'),
        });
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
        });
      },
    });
  }

  getTfrConfirmationMessageKey(): string {
    const formValue = this.selfServiceForm.getRawValue();

    if (formValue.firstTime === true && formValue.inCompany === true) {
      return 'selfservice-request.tfr-declaration.confirmation.first-time-company.label';
    }

    if (formValue.firstTime === true) {
      return 'selfservice-request.tfr-declaration.confirmation.first-time-fund.label';
    }

    if (formValue.alreadyInCompany === true) {
      return 'selfservice-request.tfr-declaration.confirmation.already-in-company.label';
    }

    if (formValue.ransom === true) {
      return 'selfservice-request.tfr-declaration.confirmation.ransom.label';
    }

    return 'selfservice-request.tfr-declaration.confirmation.not-ransom.label';
  }

  isTfrInCompanySelected(): boolean {
    return this.selfServiceForm.get('inCompany')?.value === true;
  }

  private enableTfrInCompanyModificationIfEligible(): void {
    if (this.selfServiceType !== RecrutimentSelfServiceType.TFR_DECLARATION || !this.isCompleted) {
      return;
    }

    const formValue = this.selfServiceForm.getRawValue();
    if (formValue.firstTime !== true || formValue.inCompany === true) {
      return;
    }

    this.tfrInCompanyModificationMode = true;
    this.selfServiceForm.get('inCompany')?.enable();
  }

  private updateTfrDeclarationSubmission(): void {
    if (!this.requestId) return;

    this.isSaving = true;
    this.preEmploymentActivitiesService.updateTfrDeclarationSubmission(this.requestId, { inCompany: true }).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = false;
        this.isCompleted = true;
        this.tfrInCompanyModificationMode = false;
        this.selfServiceForm.disable();
        this.submitted.emit();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-success-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-success-message'),
        });
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
        });
      },
    });
  }

  private submit(): void {

    if (!this.requestId) return;

    const mapper = SELF_SERVICE_MAPPERS[this.selfServiceType]

    if (!mapper) return;

    const payload: SelfserviceSelfServiceSubmission = {
      activityKind: 'SELF_SERVICE',
      selfService: mapper(this.selfServiceForm.getRawValue()),
    };

    this.isSaving = true;

    this.preEmploymentActivitiesService.submitActivity(this.requestId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDraft = false;
        this.isCompleted = true;
        this.selfServiceForm.disable();
        this.enableTfrInCompanyModificationIfEligible();
        this.submitted.emit();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-success-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-success-message'),
        });
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
          detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
        });
      },
    });
  }

  onClose(): void {
    this.selfServiceForm.reset();
    this.formLoaded = false;
    this.isCompleted = false;
    this.isDraft = false;
    this.tfrConfirmationVisible = false;
    this.tfrInCompanyModificationMode = false;
    this.close.emit();
  }
}